import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

export const useGetMultipleContacts = ({
    domain,
    attendeeNames,
    enabled = true,
}: {
    domain: string;
    attendeeNames: string[];
    enabled?: boolean;
}) => {
    const contactQueries = useQueries({
        queries: attendeeNames.map((name) => ({
            queryKey: aiAgentQueryKeys.contacts(domain, name, undefined),
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
                try {
                    return await salesApiService.aiAgent.meeting.getContacts(
                        { domain, search: name?.trim() },
                        { cancellation: signal },
                    );
                } catch (error) {
                    if (error instanceof Error && error.name === "AbortError") {
                        return [];
                    }
                    throw error;
                }
            },
            enabled: enabled && Boolean(name?.trim()),
        })),
    });

    const data: Contact[] = useMemo(() => {
        return contactQueries
            .filter((query) => {
                if (!query.data) {
                    return false;
                }
                if (query.isError && query.error instanceof Error && query.error.name === "AbortError") {
                    return false;
                }
                return Array.isArray(query.data);
            })
            .flatMap((query) => {
                const queryData = query.data;
                return Array.isArray(queryData) ? queryData : [];
            });
    }, [contactQueries]);

    const isLoading = contactQueries.some((query) => query.isLoading);
    const isError = contactQueries.some(
        (query) => query.isError && !(query.error instanceof Error && query.error.name === "AbortError"),
    );
    const error = contactQueries.find(
        (query) => query.error && !(query.error instanceof Error && query.error.name === "AbortError"),
    )?.error;

    return {
        data,
        isLoading,
        isError,
        error,
    };
};
